import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PALETTE } from "../tokens";

/* ──────────────────────────────────────────────────────────────────
 * FeedCard
 *
 * Single signal in the Haiven Intelligence Feed. Renders collapsed
 * by default (pill + headline + 2-line body excerpt + "See more")
 * and expands on tap to surface WHAT IT MEANS plain-English copy
 * plus a single gold WHAT TO DO call-to-action that routes the
 * user into the most-relevant in-app surface.
 *
 * The pill label retains its clinical wording (INSIGHT / PATTERN /
 * REFLECTION / CROSS / PROTOCOL). Tapping the small "?" beside the
 * label opens a 1-sentence definition popover — clinical first,
 * plain-English on demand. Only one popover open at a time across
 * the feed; clicking outside dismisses.
 *
 * Props:
 *   item        — FeedItemOut from the backend (see app/api/feed.py).
 *                 Carries optional ``action_text`` and ``action_route``
 *                 fields computed at query time; the card renders the
 *                 default CTA when either is missing.
 *   onMarkRead  — optional (itemId) => void. Called on first expand
 *                 if the item is currently unread.
 *
 * ``/report`` actions navigate to the in-app report preview screen.
 * (The old ``onShareReport`` callback prop opened the share modal
 * directly — retired when /report became a real route; preview-first
 * is the point of that screen.)
 * ────────────────────────────────────────────────────────────────── */

const PILL_DEFINITIONS = {
  INSIGHT:    "A specific signal our engine flagged.",
  PATTERN:    "A trend tracked over multiple days.",
  REFLECTION: "Two signals that may be connected.",
  CROSS:      "Two signals that may be connected.",
  PROTOCOL:   "Your follow-through consistency in the data.",
};

const PILL_STYLES = {
  INSIGHT:    { bg: "#E4DDD0", color: "#9E8456" },
  PATTERN:    { bg: "#E4DDD0", color: "#1C1814" },
  REFLECTION: { bg: PALETTE.bg, color: PALETTE.textMute },
  CROSS:      { bg: PALETTE.bg, color: PALETTE.textMute },
  PROTOCOL:   { bg: PALETTE.surfaceAlt, color: PALETTE.dark },
};

// Singleton popover state. Holds the currently-open feed-item id
// so a second tap anywhere in the feed closes the prior popover
// before opening a new one. Lives at module scope so it isn't
// reset by individual FeedCard re-renders.
let _openPillId = null;
const _pillListeners = new Set();
function _setOpenPill(id) {
  _openPillId = id;
  _pillListeners.forEach((fn) => fn(id));
}
function _useOpenPill() {
  const [val, setVal] = useState(_openPillId);
  useEffect(() => {
    _pillListeners.add(setVal);
    return () => { _pillListeners.delete(setVal); };
  }, []);
  return [val, _setOpenPill];
}


/**
 * Plain-English "WHAT THIS MEANS" copy. Three keyword buckets
 * matching the same shape as the backend action mapper — keeps the
 * spoken-language sentence consistent with the route the user
 * lands on. Returns null when no bucket matches so the section can
 * be hidden rather than showing a generic line.
 */
function whatThisMeans(title) {
  if (!title) return null;
  const t = title.toLowerCase();
  if (t.includes("hrv") && (t.includes("volatil") || t.includes("low"))) {
    return "Your rest and energy signals are pointing in different directions today.";
  }
  if (t.includes("resting") && t.includes("spike")) {
    return "A change like this today reads more like a one-off than a trend.";
  }
  if (t.includes("adherence") || t.includes("protocol")) {
    return "Consistency compounds. Even partial completion builds the habit signal.";
  }
  if (t.includes("sleep") && (t.includes("decouple") || t.includes("decoupling"))) {
    return "Your rest and energy signals are telling different stories today — worth a closer look.";
  }
  return null;
}


/**
 * "Based on:" data-source line. Falls back to the raw ``source``
 * field from the feed item when no explicit metadata is available.
 * Cheap to extend later if the backend adds a structured
 * ``data_window`` field.
 */
function dataSourceLine(item) {
  if (!item) return null;
  if (item.source) return `Based on: ${item.source}`;
  return null;
}


function truncate(text, lines = 2, charsPerLine = 60) {
  if (!text) return "";
  const limit = lines * charsPerLine;
  if (text.length <= limit) return text;
  // Break on the last sentence boundary that fits, falling back to
  // a word boundary, falling back to a hard cut. Keeps the preview
  // from ending mid-word for the common short-paragraph case.
  const head = text.slice(0, limit);
  const lastDot = head.lastIndexOf(". ");
  if (lastDot > limit * 0.6) return text.slice(0, lastDot + 1) + " …";
  const lastSpace = head.lastIndexOf(" ");
  if (lastSpace > 0) return text.slice(0, lastSpace) + "…";
  return head + "…";
}


// Local YYYY-MM-DD for the user's calendar — used to detect when a
// feed item's reference_date IS today, in which case the "Check
// your journal from that day" CTA would route to the live blank
// flow (counterproductive: the user came to look back, not start
// a new entry). We render an inline note instead.
function _localTodayKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}


export function FeedCard({ item, onMarkRead }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [openPillId, setOpenPillId] = _useOpenPill();
  const [todayNoticeShown, setTodayNoticeShown] = useState(false);
  const cardRef = useRef(null);

  const pillKey = (item?.type || "").toUpperCase();
  const pill = PILL_STYLES[pillKey] || { bg: PALETTE.surfaceAlt, color: PALETTE.textMid };
  const definition = PILL_DEFINITIONS[pillKey];
  const popoverOpen = openPillId === item.id;

  const meaning = whatThisMeans(item.title);
  const based = dataSourceLine(item);
  const actionText = item.action_text || "Check in today to sharpen this signal.";
  const actionRoute = item.action_route || "/checkin";

  // Outside-tap dismiss for the pill popover. Bound only while a
  // popover is open so the listener doesn't fire for every tap on
  // the feed.
  useEffect(() => {
    if (!popoverOpen) return undefined;
    const handle = (e) => {
      // Allow tapping the trigger again to toggle off cleanly.
      const trigger = cardRef.current?.querySelector("[data-pill-trigger]");
      if (trigger && trigger.contains(e.target)) return;
      const popover = cardRef.current?.querySelector("[data-pill-popover]");
      if (popover && popover.contains(e.target)) return;
      setOpenPillId(null);
    };
    document.addEventListener("mousedown", handle);
    document.addEventListener("touchstart", handle, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("touchstart", handle);
    };
  }, [popoverOpen, setOpenPillId]);

  const togglePopover = (e) => {
    // Stop the click from also toggling the card expansion when the
    // user just wanted to read the definition.
    e.stopPropagation();
    setOpenPillId(popoverOpen ? null : item.id);
  };

  const toggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      if (next && !item.read && onMarkRead) onMarkRead(item.id);
      return next;
    });
  };

  const handleAction = (e) => {
    // Stop the click from collapsing the card under the gold link.
    e.stopPropagation();
    // /report routes to the in-app preview screen — the user sees the
    // report before any share decision.
    if (actionRoute === "/report") {
      navigate("/report");
      return;
    }
    // Context-aware /checkin: when the feed item is anchored to a
    // specific past date, route to /checkin?date=YYYY-MM-DD so the
    // CheckIn screen renders its historical read-only view instead
    // of today's blank form. /protocol always means "today" so it
    // never takes a date param. Other routes pass through.
    if (actionRoute === "/checkin" && item.reference_date) {
      // When the anchor IS today, there's nothing to look back at —
      // the historical view would resolve to a partial day still in
      // motion. Show an inline note instead of navigating.
      if (item.reference_date === _localTodayKey()) {
        setTodayNoticeShown(true);
        return;
      }
      navigate(`/checkin?date=${encodeURIComponent(item.reference_date)}`);
      return;
    }
    navigate(actionRoute);
  };

  const timeAgo = (() => {
    if (!item.created_at) return "";
    const diff = Date.now() - new Date(item.created_at).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  })();

  return (
    <div
      ref={cardRef}
      id={`feed-card-${item.id}`}
      className="feed-card"
      onClick={toggleExpanded}
      style={{
        position: "relative",
        background: "#FDFAF6",
        border: `1px solid ${PALETTE.border}`,
        borderRadius: 14,
        padding: "16px 18px",
        marginBottom: 14,
        cursor: "pointer",
        transition: "background 0.2s ease, box-shadow 0.2s ease",
        boxShadow: expanded ? "0 4px 18px rgba(28,24,20,0.08)" : "0 1px 6px rgba(28,24,20,0.04)",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      {/* Top row — pill + ? + domain + time */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      }}>
        <span className="pill" style={{
          background: pill.bg, color: pill.color,
          fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
          padding: "3px 9px", borderRadius: 99, fontWeight: 500,
        }}>
          {pillKey || "SIGNAL"}
        </span>
        {definition && (
          <button
            data-pill-trigger
            onClick={togglePopover}
            aria-label={`What does ${pillKey} mean?`}
            style={{
              width: 18, height: 18, minWidth: 18,
              borderRadius: "50%",
              border: `1px solid ${PALETTE.primary}`,
              background: "transparent",
              color: PALETTE.primary,
              fontSize: 10, lineHeight: "16px",
              padding: 0, margin: 0,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            ?
          </button>
        )}
        {item.domain && (
          <span style={{
            fontSize: 10, color: PALETTE.textFaint,
            letterSpacing: "0.07em", textTransform: "uppercase",
          }}>
            {item.domain}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {!item.read && (
          <span style={{
            width: 6, height: 6, borderRadius: "50%",
            background: PALETTE.primary, flexShrink: 0,
          }} />
        )}
        {timeAgo && (
          <span style={{ fontSize: 10, color: PALETTE.textFaint }}>{timeAgo}</span>
        )}
      </div>

      {popoverOpen && definition && (
        <div
          data-pill-popover
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: 44, left: 14, zIndex: 30,
            maxWidth: 220,
            background: "#F2EDE3",
            color: "#1C1814",
            border: `1px solid ${PALETTE.border}`,
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 12.5,
            lineHeight: 1.5,
            fontWeight: 300,
            boxShadow: "0 6px 18px rgba(28,24,20,0.14)",
          }}
        >
          {definition}
        </div>
      )}

      {/* Headline */}
      <p
        className="serif"
        style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 16, lineHeight: 1.35,
          color: "#1C1814",
          fontWeight: 500,
          marginTop: 2,
        }}
      >
        {item.title}
      </p>

      {/* Body — truncated when collapsed, full when expanded */}
      <p style={{
        fontSize: 13, lineHeight: 1.65,
        color: PALETTE.textMid, fontWeight: 300,
        whiteSpace: expanded ? "pre-wrap" : "normal",
        transition: "max-height 0.2s ease",
      }}>
        {expanded ? item.body : truncate(item.body, 2, 60)}
      </p>

      {/* Expanded sections */}
      <div style={{
        maxHeight: expanded ? 800 : 0,
        overflow: "hidden",
        transition: "max-height 0.2s ease",
      }}>
        {expanded && (
          <div style={{ paddingTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
            {based && (
              <div>
                <p style={{
                  fontSize: 9, letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: PALETTE.textMute, fontWeight: 500,
                  marginBottom: 4,
                }}>
                  Data source
                </p>
                <p style={{
                  fontSize: 12, lineHeight: 1.55,
                  color: PALETTE.textMid, fontWeight: 300,
                  fontStyle: "italic",
                }}>
                  {based}
                </p>
              </div>
            )}
            {meaning && (
              <div>
                <p style={{
                  fontSize: 9, letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: PALETTE.textMute, fontWeight: 500,
                  marginBottom: 4,
                }}>
                  What this means
                </p>
                <p style={{
                  fontSize: 13, lineHeight: 1.6,
                  color: "#1C1814", fontWeight: 400,
                }}>
                  {meaning}
                </p>
              </div>
            )}
            <div>
              <p style={{
                fontSize: 9, letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: PALETTE.textMute, fontWeight: 500,
                marginBottom: 4,
              }}>
                What to do
              </p>
              <button
                onClick={handleAction}
                style={{
                  background: "none", border: "none", padding: 0,
                  cursor: "pointer", textAlign: "left",
                  minHeight: 44,
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13.5, lineHeight: 1.5,
                  color: PALETTE.primary, fontWeight: 500,
                  letterSpacing: "0.01em",
                }}
              >
                {actionText} →
              </button>
              {todayNoticeShown && (
                <p
                  role="status"
                  style={{
                    marginTop: 8,
                    fontSize: 12.5, lineHeight: 1.55,
                    color: PALETTE.textMid, fontWeight: 300,
                    fontStyle: "italic",
                  }}
                >
                  This insight is based on today's data. Check in to add more context.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Collapsed-state affordance */}
      {!expanded && (
        <div style={{
          display: "flex", justifyContent: "flex-end",
          marginTop: 2,
        }}>
          <span style={{
            fontSize: 11.5, color: PALETTE.primary,
            fontWeight: 500, letterSpacing: "0.02em",
          }}>
            See more ›
          </span>
        </div>
      )}
    </div>
  );
}
